import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Store {
  _id: string;
  name: string;
  category: string;
  location: {
    address: string;
  };
  phone: string;
  isOpen: boolean;
  preparationTime?: number;
}

interface StoreListProps {
  selectedCategory: string;
  onSelectStore: (store: Store) => void;
}

const API_URL = 'https://api.bringit.co.in/api';

const StoreList: React.FC<StoreListProps> = ({ selectedCategory, onSelectStore }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    fetchCategoryName();
  }, [selectedCategory]);

  const fetchCategoryName = async () => {
    if (selectedCategory === 'all') {
      setCategoryName('all');
      fetchStores('all');
      return;
    }

    try {
      // Fetch category details to get the name
      const response = await axios.get(`${API_URL}/categories`);
      console.log('All categories:', response.data);
      const category = response.data.find((cat: any) => cat._id === selectedCategory);
      console.log('Selected category ID:', selectedCategory);
      console.log('Found category:', category);
      const catName = category ? category.name : selectedCategory;
      console.log('Category name to filter by:', catName);
      setCategoryName(catName);
      fetchStores(catName);
    } catch (error) {
      console.error('Error fetching category:', error);
      // Fallback: try using selectedCategory as name directly
      setCategoryName(selectedCategory);
      fetchStores(selectedCategory);
    }
  };

  const fetchStores = async (catName: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/stores`, {
        params: { isActive: true }
      });
      
      console.log('All stores from API:', response.data);
      let filteredStores = response.data.filter((store: Store) => store.isOpen);
      console.log('Open stores:', filteredStores);
      
      if (catName !== 'all') {
        console.log('Filtering by category:', catName);
        filteredStores = filteredStores.filter((store: Store) => {
          const matches = store.category && store.category.toLowerCase() === catName.toLowerCase();
          console.log(`Store ${store.name}: category="${store.category}", matches=${matches}`);
          return matches;
        });
      }
      
      console.log('Final filtered stores:', filteredStores);
      setStores(filteredStores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: any = {
      grocery: '🛒',
      pharmacy: '💊',
      vegetables: '🥬',
      meat: '🥩',
      bakery: '🍞',
      dairy: '🥛',
      restaurant: '🍽️',
      sweets: '🍬',
      beverages: '🥤',
      petcare: '🐾',
      flowers: '🌸',
      electronics: '📱',
      hardware: '🔧',
      general: '🏪'
    };
    return icons[category] || '🏪';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ 
          display: 'inline-block',
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <span style={{ fontSize: '60px' }}>🏪</span>
        <h3 style={{ marginTop: '15px', color: '#666' }}>No stores available</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>
          {selectedCategory === 'all' 
            ? 'No stores are currently open in your area'
            : 'No stores in this category are currently open'}
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 15px' }}>
      <h2 style={{ 
        fontSize: '22px', 
        fontWeight: '700', 
        marginBottom: '15px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        🛍️ Shop by Store
      </h2>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        Choose your favorite store • స్టోర్ ఎంచుకోండి
      </p>
      
      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="🔍 Search stores... | స్టోర్లు వెతకండి"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            border: '2px solid #e0e0e0',
            borderRadius: '12px',
            outline: 'none',
            transition: 'all 0.3s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
        />
      </div>
      
      <div style={{ display: 'grid', gap: '15px' }}>
        {stores
          .filter(store => 
            store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store.location.address.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((store) => (
          <div
            key={store._id}
            onClick={() => onSelectStore(store)}
            style={{
              background: 'white',
              borderRadius: '15px',
              padding: '15px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: '1px solid #f0f0f0',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '32px' }}>{getCategoryIcon(store.category)}</span>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#333' }}>
                      {store.name}
                    </h3>
                    <span style={{ 
                      fontSize: '11px', 
                      color: 'white',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '2px 8px',
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      display: 'inline-block',
                      marginTop: '4px'
                    }}>
                      {store.category}
                    </span>
                  </div>
                </div>
                
                <p style={{ 
                  fontSize: '13px', 
                  color: '#666', 
                  margin: '8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <span>📍</span> {store.location.address}
                </p>
                
                <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600' }}>
                    ✅ Open Now
                  </span>
                  {store.preparationTime && (
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      ⏱️ {store.preparationTime} mins
                    </span>
                  )}
                </div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                →
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoreList;
